import { CSSProperties, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { AgenticViewer } from '@amzn/work-spaces-agentic-web-viewer';
import '@amzn/work-spaces-agentic-web-viewer/styles.css';
import { CoreFactory } from '@/bridge/factory/CoreFactory';
import { useReportHeartbeatTask } from '@/hooks/useReportHeartbeatTask';
import { useSessionStore } from '@/stores/session';
import { isAgenticWorkspace } from '@views/Stream/agentic';
import { handleHeartbeatError } from '@views/Stream/heartbeatError';

// The viewer logs through the client's logger, so its lines reach the upload.
const logger = CoreFactory.getLogger();

// Where scripts/copy-dcv-assets.sh serves the DCV SDK's workers, as for Maxibon.
const DCV_ASSETS_PATH = '/static/dcvjs/';

// The viewer's own page uses this, so it pads for the phone's safe areas. Not
// Maxibon's viewport: its overlays-content mode lets the keyboard cover the chat.
const AGENTIC_VIEWPORT =
  'width=device-width, initial-scale=1.0, viewport-fit=cover';

// The docking shell needs a fixed box that page reflow cannot disturb. Four
// edges, not 100vh: a phone's 100vh runs behind a visible browser bar.
const containerStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  zIndex: 1000,
};

const hasWspContextHandoff = (): boolean => {
  try {
    return window.sessionStorage.getItem('wsp_context') !== null;
  } catch {
    // Storage is unavailable, so there is no handoff to read.
    return false;
  }
};

// With no wsp_context handoff, or on a standard WorkSpace, there is no agent.
const AgenticRoute: React.FC = () => {
  // Persisted in sessionStorage, so it survives a reload of /agentic.
  const resourceId = useSessionStore((state) => state.resourceId);
  // Routing only. The WorkSpace host enforces access.
  const resourceDetails = useSessionStore(
    (state) => state.resource?.ResourceDetails
  );
  const routeShowsViewer =
    hasWspContextHandoff() && isAgenticWorkspace(resourceDetails);
  const { startReportingHeartbeat } = useReportHeartbeatTask({
    onError: handleHeartbeatError,
  });

  // Maxibon routes here before it starts the heartbeat. Without one, the broker
  // ends a SAML or IAM Identity Center session about 25 minutes after sign-in.
  useEffect(() => {
    if (routeShowsViewer) {
      startReportingHeartbeat();
    }
  }, [routeShowsViewer]);

  // Unlike Maxibon, no safe-area-insets class: the viewer pads for the insets.
  useEffect(() => {
    if (!routeShowsViewer) {
      return;
    }
    // A page restored from the back/forward cache holds a dead DCV connection.
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        logger.info(
          'Page rendered from b/f cache, forcing reload to establish connection'
        );
        window.location.reload();
      }
    };
    window.addEventListener('pageshow', handlePageShow);

    const viewportMeta = document.querySelector<HTMLMetaElement>(
      'meta[name="viewport"]'
    );
    const previousViewport = viewportMeta?.getAttribute('content');
    viewportMeta?.setAttribute('content', AGENTIC_VIEWPORT);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      if (viewportMeta && previousViewport != null) {
        viewportMeta.setAttribute('content', previousViewport);
      }
    };
  }, [routeShowsViewer]);

  if (!routeShowsViewer) {
    return <Navigate to="/" replace />;
  }

  return (
    <div style={containerStyle}>
      <AgenticViewer
        workspaceId={resourceId || undefined}
        logger={logger}
        dcvBaseUrl={DCV_ASSETS_PATH}
      />
    </div>
  );
};

export default AgenticRoute;
